/**
 * Backend API Client
 *
 * Refactored from lib/api.ts to focus purely on backend communication
 * without any Supabase dependencies
 */

import { authService } from './authService'
import type {
  SongCreate,
  SongUpdate,
  SongSettingsUpdate,
  SongSettingsResponse,
  SongListResponse,
  SongResponse
} from '../types/song'
import type { EditorPreferences } from '../types/preferences'
import type {
  Word,
  DiscoveryMetadata,
  CategorizeCollectionRequest,
  CategorizeCollectionResponse,
  CategorizeWordsRequest,
  CategorizeWordsResponse,
  RecategorizeGroupRequest,
  RecategorizeGroupResponse,
} from '../types/wordCollection'
import type {
  RhymeSearchResponse,
  ThesaurusIndexResponse,
  ThesaurusSynonymResponse,
  ThesaurusEntry
} from '../types/wordTools'

// Word collection API response types
interface WordListResponse {
  words: Word[]
  total: number
}

interface WordResponse {
  message: string
  word: Word
}

// In production, use relative URLs (nginx proxies /api/ to backend)
// In test, use localhost for direct backend access
const API_BASE_URL = typeof process !== 'undefined' && process.env.NODE_ENV === 'test'
  ? 'http://localhost:8001'  // Test environment
  : ''  // Empty string = relative URLs, proxied by nginx in production

class BackendApiClient {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const { data: { session }, error } = await authService.getSession()

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (error) {
      console.warn('Error getting session:', error)
    }

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`
    }

    return headers
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    let headers = await this.getAuthHeaders()

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    })

    if (!response.ok) {
      // Handle 401 Unauthorized responses
      if (response.status === 401) {
        console.warn('Received 401, attempting to refresh session...')

        // Try to refresh the session
        const { error } = await authService.refreshSession()

        if (!error) {
          console.log('Session refreshed successfully, retrying request')

          // Retry with new token
          headers = await this.getAuthHeaders()
          const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers: {
              ...headers,
              ...options.headers,
            },
          })

          if (retryResponse.ok) {
            // Handle 204 No Content (common for DELETE)
            if (retryResponse.status === 204) {
              return undefined as T
            }
            return retryResponse.json()
          }
        }

        // If refresh failed or retry still got 401, handle session expiration
        console.warn('Session refresh failed or retry unsuccessful, handling session expiration')
        await authService.handleSessionExpired()
        throw new Error('Session expired. Please log in again.')
      }

      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }

    // Handle 204 No Content (common for DELETE)
    if (response.status === 204) {
      return undefined as T
    }

    return response.json()
  }

  // Song API methods
  async createSong(songData: SongCreate): Promise<SongResponse> {
    return this.request<SongResponse>('/api/songs/', {
      method: 'POST',
      body: JSON.stringify(songData),
    })
  }

  async getSong(songId: string): Promise<SongResponse> {
    return this.request<SongResponse>(`/api/songs/${songId}`)
  }

  async listSongs(
    page: number = 1,
    perPage: number = 10,
    status?: string
  ): Promise<SongListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    })

    if (status) {
      params.append('status', status)
    }

    return this.request<SongListResponse>(`/api/songs/?${params}`)
  }

  async updateSong(songId: string, songData: SongUpdate): Promise<SongResponse> {
    return this.request<SongResponse>(`/api/songs/${songId}`, {
      method: 'PUT',
      body: JSON.stringify(songData),
    })
  }

  async deleteSong(songId: string): Promise<void> {
    await this.request<void>(`/api/songs/${songId}`, {
      method: 'DELETE',
    })
  }

  // Song Settings API methods
  async getSongSettings(songId: string): Promise<SongSettingsResponse> {
    return this.request<SongSettingsResponse>(`/api/songs/${songId}/settings`)
  }

  async updateSongSettings(songId: string, settingsData: SongSettingsUpdate): Promise<SongSettingsResponse> {
    return this.request<SongSettingsResponse>(`/api/songs/${songId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    })
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string; database: string }> {
    return this.request<{ status: string; timestamp: string; database: string }>('/health')
  }

  // User Preferences API methods
  async getUserPreferences(): Promise<EditorPreferences> {
    // Backend returns { preferences: {...} }, unwrap it
    const response = await this.request<{ preferences: EditorPreferences }>('/api/user/preferences')
    return response.preferences
  }

  async updateUserPreferences(preferences: Partial<EditorPreferences>): Promise<EditorPreferences> {
    // Backend expects { preferences: {...} } and returns same wrapper
    const response = await this.request<{ preferences: EditorPreferences }>('/api/user/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ preferences }),
    })
    return response.preferences
  }

  // Word Collection API methods
  async getWordCollection(songId: string): Promise<WordListResponse> {
    return this.request<WordListResponse>(`/api/songs/${songId}/word-collection`)
  }

  async addWord(
    songId: string,
    wordData: { word: string; id?: string; discovery?: DiscoveryMetadata; notes?: string }
  ): Promise<WordResponse> {
    return this.request<WordResponse>(`/api/songs/${songId}/word-collection`, {
      method: 'POST',
      body: JSON.stringify(wordData),
    })
  }

  async updateWord(songId: string, wordId: string, updateData: Partial<Word>): Promise<WordResponse> {
    return this.request<WordResponse>(`/api/songs/${songId}/word-collection/${wordId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    })
  }

  async deleteWord(songId: string, wordId: string): Promise<void> {
    await this.request<void>(`/api/songs/${songId}/word-collection/${wordId}`, {
      method: 'DELETE',
    })
  }

  // Scope 06: Rhyme collection methods removed from frontend.
  // Scope 07: Backend rhyme endpoints marked deprecated (still functional for backward compat).

  // Word Tools AI API methods
  async searchRhymes(word: string, stability?: string): Promise<RhymeSearchResponse> {
    return this.request<RhymeSearchResponse>('/api/word-tools/rhymes', {
      method: 'POST',
      body: JSON.stringify({ word, stability: stability || 'neutral' }),
    })
  }

  async searchThesaurusIndex(word: string, context?: string): Promise<ThesaurusIndexResponse> {
    return this.request<ThesaurusIndexResponse>('/api/word-tools/thesaurus/index', {
      method: 'POST',
      body: JSON.stringify({ word, context }),
    })
  }

  async searchThesaurusSynonyms(
    word: string,
    selectedEntries: ThesaurusEntry[],
    context?: string
  ): Promise<ThesaurusSynonymResponse> {
    return this.request<ThesaurusSynonymResponse>('/api/word-tools/thesaurus/synonyms', {
      method: 'POST',
      body: JSON.stringify({ word, selected_entries: selectedEntries, context }),
    })
  }

  // Rhyme categorization endpoints (Scope 13)
  async categorizeCollection(request: CategorizeCollectionRequest): Promise<CategorizeCollectionResponse> {
    return this.request<CategorizeCollectionResponse>('/api/word-tools/categorize-collection', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async categorizeWords(request: CategorizeWordsRequest): Promise<CategorizeWordsResponse> {
    return this.request<CategorizeWordsResponse>('/api/word-tools/categorize-words', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async recategorizeGroup(request: RecategorizeGroupRequest): Promise<RecategorizeGroupResponse> {
    return this.request<RecategorizeGroupResponse>('/api/word-tools/recategorize-group', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }
}

export const backendApiClient = new BackendApiClient()

// Re-export for backward compatibility
export const apiClient = backendApiClient
